/**
 * The tour's steps and words, read by both clients (src/components/Tutorial.tsx and
 * client/lib/tour-steps.ts), so the two tours are the same cards in the same order by construction.
 *
 * The first run is four steps: the board, Add a task, Done and Profile (docs/ui-audit.md, anti-pattern
 * triage item 7). Everything else is a feature step, shown once per device in that feature's own
 * tour, one tour per app open (shared/tours.ts).
 *
 * A step's `target` is a tour target name: the native element carries `dataSet={{ tour: "<name>" }}`
 * (the web build renders `data-tour="<name>"`), the Preact tour maps the name to a CSS selector
 * (Tutorial.tsx SELECTORS). "a|b" means the first of either in document order.
 *
 * The spoken line must stay word for word: public/audio/tour/manifest.json holds the recording of
 * each card by its title and `spoken` (scripts/make-tour-voice.py), and a changed line goes silent.
 * The words on the card (`body`) are plain English; what the peon says (`spoken`) is his own and
 * need not match (owner, 2026-10-04).
 *
 * Pure on purpose: no platform imports.
 */
import type { FlagKey } from "./types";

export type TourRoute = "open" | "done" | "rewards" | "task" | "upgrades" | "peon";

/**
 * When a card plays on its own (owner, 2026-10-04: "Rather than play the full tutorial up front,
 * play the relevant segments of it as things occur naturally, as in when someone opens a task for
 * the first time, explain how it works, when someone completes a task, explain gold and rewards").
 * "first-open": the board's first showing on this device. "board": the board showing a thing for
 * the first time (Next up, the note button), one such moment per app open so a newcomer is not
 * buried. "task-opened": a task screen opening. "task-done": a job finished, back on the board.
 * "profile-opened", "rewards-opened", "upgrades-opened": those screens opening. "badges-shown": the
 * first badge showing, in Profile (docs/achievements.md). The question mark in Profile
 * still plays the whole tour in order.
 */
export type TourMoment = "first-open" | "board" | "task-opened" | "task-done" | "profile-opened" | "rewards-opened" | "upgrades-opened" | "peon-opened" | "badges-shown";

export interface TourStep {
  /** The moment this card plays on its own, the first time it happens on the device (shared/tours.ts stepsForMoment). */
  moment: TourMoment;
  /** Tour target name of the element to spotlight; a centred card over the full dim if absent. */
  target: string | null;
  title: string;
  /** The words on the card: plain English, the gist (docs/product.md "Tour and hint copy gives the gist only"). */
  body: string;
  /**
   * What the peon says for the card, in his own voice; it need not match the words on the card
   * (owner, 2026-10-04: "What the peon says during the tutorial doesn't need to match the text.
   * The written text shouldn't be in peon speak, just what he says is"). Recorded by
   * scripts/make-tour-voice.py and found in the manifest by title and this line.
   */
  spoken: string;
  /** Only shown in this feature's tour (never in the first run). */
  feature?: FlagKey;
  /** Screen the step is shown on; the tour navigates there. Defaults to the open board. "task" opens the first open task. */
  route?: TourRoute;
  /** The body while the bounty UI is off (nothing on the board pays), when it differs. */
  bodyPlain?: string;
  /**
   * What the step's target needs on the board, known before the first card shows so the count
   * never changes mid-tour: "task", an open task; "nextUp", an open chore, urgent or luxury task.
   * A step without it is left out.
   */
  needs?: "task" | "nextUp";
  /** With no open task, the step is shown on the open board instead, untargeted, with this body. */
  bodyNoTask?: string;
  /** What the peon says for the bodyNoTask reading. */
  spokenNoTask?: string;
  /** What the peon says for the bodyPlain reading. */
  spokenPlain?: string;
}

export const TOUR_STEPS: readonly TourStep[] = [
  // The first run: the board behind the welcome, then the three things a new peon needs.
  {
    moment: "first-open",
    route: "open",
    target: null,
    title: "Welcome, peon",
    spoken: "Me keep the list of what need doing. Photo before, photo after. Me show you the rest as you go.",
    body: "Omie's Peons keeps track of what needs doing around the house, with a photo before and a photo after. More as you go.",
  },
  {
    moment: "first-open",
    route: "open",
    target: "fab",
    title: "Add a task",
    spoken: "Tap the plus. Say the job, snap photo of the mess. Mess always there. Tick Urgent! if it can't wait.",
    body: "Tap the plus, describe the job and snap a photo of the mess. Tick Urgent! if it can't wait.",
  },
  // A task's own screen, the first time one opens.
  {
    moment: "task-opened",
    route: "task",
    target: "job-done",
    title: "A job",
    spoken: "Photo show the mess. When it done, tap Job's Done and snap the after photo. Then me nap.",
    body: "The photo shows the mess. When it's done, tap Job's Done and snap the after photo.",
    bodyNoTask: "Tap a task to see its photo. When it's done, tap Job's Done and snap the after photo.",
    spokenNoTask: "Tap a task to see the mess. When it done, tap Job's Done and snap the after photo.",
  },
  {
    moment: "task-done",
    route: "done",
    target: "tab-done",
    title: "What got done",
    spoken: "Everything finished live in Done. Before, after, who did it. Me remember. Mostly.",
    body: "Everything finished lives in Done, with before and after photos and who did it.",
  },
  // You: the first time Profile opens.
  {
    moment: "profile-opened",
    route: "open",
    target: "who",
    title: "That's you",
    spoken: "Turn on notifications here, so me can nag when chore need doing. Or change who this phone belong to. Work, work!",
    body: "Turn on notifications here for when a chore needs doing, or change who this device belongs to.",
  },
  // Your peon: the first time the character editor opens (owner, 2026-10-04: "There should be a
  // tutorial step for it").
  {
    moment: "peon-opened",
    route: "peon",
    target: "editor-tabs",
    title: "Dress your peon",
    spoken: "Tap a piece, me wear it. Some cost gold. Do jobs, get gold, buy. Crown stay with the Peon of the Month.",
    body: "Tap a piece to wear it. Some cost gold: earn it with jobs, then buy. The crown stays with the Peon of the Month.",
  },
  // The feature tours, each once per device when its flag is on.
  {
    feature: "focusUI",
    moment: "board",
    route: "open",
    target: "next-up",
    needs: "nextUp",
    title: "Next up",
    spoken: "Chores to do first sit here. The rest wait in a pile below. Pull past the end, pile fan out.",
    body: "The chores to do first sit here. Everything else waits in a pile below. Pull past the end to fan it out.",
  },
  {
    feature: "notesUI",
    moment: "board",
    route: "open",
    target: "note-add",
    title: "Pin a note",
    spoken: "Something everyone should know today? Pin note here. It sit on top for a day, then me throw it out.",
    body: "Something everyone should know today? Pin a note here. It sits at the top of the board for a day, then clears itself.",
  },
  {
    feature: "bountyUI",
    moment: "task-opened",
    route: "task",
    target: "coins",
    needs: "task",
    title: "Earn your gold",
    spoken: "Every job pay gold. One coin little, three coin lot. Finish it, me pay.",
    body: "Every job pays gold. One coin is a little, three is a lot. Finish it to collect.",
  },
  {
    feature: "quickUI",
    moment: "task-opened",
    route: "task",
    target: "coins",
    needs: "task",
    title: "Strike while it's hot",
    spoken: "Some jobs pay bonus for a while. Quick peon get more gold. Slow peon get less. Me know.",
    body: "Some tasks pay a bonus for a while, so the quick peon earns more gold.",
  },
  {
    feature: "economyUI",
    moment: "task-done",
    route: "open",
    target: "who",
    title: "Your gold, your rewards",
    spoken: "Tap Profile, see you gold. Rewards is in there. That where gold go. Go spend.",
    body: "Tap Profile to see your gold. Rewards, where you spend it, is in there.",
  },
  {
    feature: "economyUI",
    moment: "rewards-opened",
    route: "rewards",
    target: "luxury-offer",
    title: "Luxury tasks",
    spoken: "Put up gold, job sit up with the chores in purple frame. Whoever finish it get half. Me want half.",
    body: "Put up gold and the job sits up with the chores in a purple frame. Whoever finishes it gets half.",
  },
  {
    feature: "economyUI",
    moment: "rewards-opened",
    route: "rewards",
    // The heading, not the form: the form is taller than a phone screen, so a card beside it had to scroll.
    target: "rewards-heading",
    title: "Offer a reward",
    spoken: "Offer something of yours. Peon buy it, gold is yours. It stay listed until they say they got it.",
    body: "Offer something of yours. When a peon buys it, the gold is yours; it stays listed until they say they got it.",
  },
  // The first badge showing, in Profile (docs/achievements.md). Centred: it plays over
  // whichever screen showed it.
  {
    feature: "achievementsUI",
    moment: "badges-shown",
    route: "open",
    target: null,
    title: "Badges",
    spoken: "Shiny badge. For good work. It pay little bit of gold too. Me like shiny.",
    body: "A badge for a job well done. Each one pays a little gold.",
  },
  // The shared pot on its own tab (docs/upgrades.md).
  {
    feature: "upgradesUI",
    moment: "upgrades-opened",
    route: "upgrades",
    target: "tab-upgrades",
    title: "Dynasty upgrades",
    spoken: "Propose home upgrade. Photo, wood goal. Everyone chip in until funded, then somebody build it. Maybe me.",
    body: "Propose a home upgrade with a photo and a wood goal. Everyone chips in until it's funded, then someone builds it.",
  },
];

/** Shown once when the tour is skipped: how to get it back. */
export const SKIP_STEP: TourStep = {
  moment: "first-open",
  route: "open",
  target: "who",
  title: "Come back any time",
  spoken: "Tap Profile up here, then the question mark. Me give tour again. Me not mind. Much.",
  body: "Tap Profile up here, then the question mark, to see this tour again whenever you like.",
};

/**
 * The steps a tour shows. The first run (no `features`) is the steps without a feature, whatever is
 * on; a feature tour shows only the named features' steps.
 */
export function stepsFor(features?: readonly FlagKey[]): TourStep[] {
  return features
    ? TOUR_STEPS.filter((step) => step.feature !== undefined && features.includes(step.feature))
    : TOUR_STEPS.filter((step) => !step.feature);
}

/** What the board held when the tour opened, read once before the first card shows. */
export interface TourBoard {
  hasTask: boolean;
  hasNextUp: boolean;
  bountyUI: boolean;
}

/**
 * The steps as the tour will show them, decided before the first card so the count never changes
 * mid-tour: a step that needs what the board lacks is left out, a task step moves to the open board
 * untargeted when there is no task to open, and a step with a plain body uses it while the bounty
 * UI is off.
 */
export function resolveSteps(steps: readonly TourStep[], board: TourBoard): TourStep[] {
  return steps
    .filter((step) => (step.needs === "task" ? board.hasTask : step.needs === "nextUp" ? board.hasNextUp : true))
    .map((step): TourStep => {
      if (step.bodyNoTask && step.route === "task" && !board.hasTask) return { ...step, route: "open", target: null, body: step.bodyNoTask, spoken: step.spokenNoTask ?? step.spoken };
      if (step.bodyPlain && !board.bountyUI) return { ...step, body: step.bodyPlain, spoken: step.spokenPlain ?? step.spoken };
      return step;
    });
}
